import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from './../../../environments/environment';
import { User } from 'src/app/models/data/user.model';
import { catchError, switchMap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CompletionQuestionsService {
    localUser!: User;

    constructor(private http: HttpClient) { }

    getUserData() {
        const user: {
            username: string;
            token: string;
            status: number;
        } = JSON.parse(localStorage.getItem('user') as string);
        this.localUser = user;
    }

    /**
     * Update a completion question answer
     * @param questionNum The question number (43, 44, 45, or 46)
     * @param completed Whether the page is complete (true = non-empty string, false = empty string)
     * @param notApplicable Optional: explicitly set the notApplicable field (overrides backend value)
     */
    updateCompletionQuestion(questionNum: number, completed: boolean, notApplicable?: boolean | null) {
        this.getUserData();
        let headers = new HttpHeaders({
            token: this.localUser.token,
            'Content-Type': 'application/json',
        });
        // First get the current question to preserve the notApplicable field (unless explicitly provided)
        // If notApplicable is explicitly provided, we can skip the getQuestion call for better performance
        const questionObs = (notApplicable !== undefined && notApplicable !== null)
            ? of(null) // Skip getQuestion if we have explicit value
            : this.getQuestion(questionNum).pipe(catchError(() => of(null)));
        
        return questionObs.pipe(
            switchMap((question) => {
                // Use explicitly provided notApplicable value, or preserve existing from backend
                // Check if notApplicable was explicitly provided (not undefined)
                let notApplicableValue: boolean | null = null;
                if (notApplicable !== undefined && notApplicable !== null) {
                    // Explicitly provided: use the provided value (convert to true or null)
                    notApplicableValue = notApplicable === true ? true : null;
                    console.log(`updateCompletionQuestion: Using explicitly provided notApplicable value: ${notApplicableValue} for question ${questionNum}`);
                } else {
                    // Not explicitly provided: preserve existing value from backend
                    notApplicableValue = question?.notApplicable === true ? true : null;
                    console.log(`updateCompletionQuestion: Preserving notApplicable from backend: ${notApplicableValue} for question ${questionNum}`);
                }
                
                // For Type 1 (numeric) questions, use "-1" instead of empty string
                // Default to Type 1 for completion questions (43, 44, 45, 46) if question is null
                const isType1 = question?.type === 1 || (question === null && [43, 44, 45, 46].includes(questionNum));
                let answerValue = completed ? '1' : '';
                if (isType1 && !completed && (!answerValue || answerValue === '')) {
                    answerValue = '-1';
                }
                
                const requestBody: any = { answer: answerValue };
                // Include notApplicable if it was explicitly provided or was previously set
                if (notApplicableValue === true) {
                    requestBody.notApplicable = true;
                }
                
                console.log(`updateCompletionQuestion: Sending request body for question ${questionNum}:`, JSON.stringify(requestBody));
                
                return this.http.put(
                    `${environment.apiUrl}/QA/${questionNum}`,
                    requestBody,
                    { headers: headers }
                );
            }),
            catchError(error => {
                console.warn(`Failed to update completion question ${questionNum}:`, error);
                // Don't show alert - just log the error and continue
                // The backend has issues with these completion questions
                // Return empty observable to prevent breaking the flow
                return of(null);
            })
        );
    }

    /**
     * Update the NotApplicable field for a question
     * @param questionNum The question number (43, 44, or 45)
     * @param notApplicable Whether the question is not applicable (true) or not (false)
     */
    updateNotApplicable(questionNum: number, notApplicable: boolean) {
        this.getUserData();
        let headers = new HttpHeaders({
            token: this.localUser.token,
            'Content-Type': 'application/json',
        });
        // First get the current question to determine question type
        return this.getQuestion(questionNum).pipe(
            catchError(() => of(null)),
            switchMap((question) => {
                // When unchecking, set answer to -1 to indicate incomplete state
                // When checking, set answer to 1 to mark page as complete
                let currentAnswer: string;
                if (!notApplicable) {
                    // Unchecking: set to -1 for Type 1 questions (incomplete state)
                    // This follows the backend convention where AnswerStat = -1 means incomplete
                    currentAnswer = question?.type === 1 ? '-1' : '';
                } else {
                    // Checking: set to 1 for Type 1 questions to mark page as complete
                    // This marks the page as complete when notApplicable is true
                    currentAnswer = question?.type === 1 ? '1' : '';
                }
                
                // Send null instead of false when unchecking (matches API response format)
                const notApplicableValue = notApplicable ? true : null;
                return this.http.put(
                    `${environment.apiUrl}/QA/${questionNum}`,
                    { 
                        answer: currentAnswer,
                        notApplicable: notApplicableValue 
                    },
                    { headers: headers }
                );
            }),
            catchError(error => {
                console.warn(`Failed to update NotApplicable for question ${questionNum}:`, error);
                if (error.error) {
                    console.warn('Error details:', error.error);
                }
                // If the API doesn't support notApplicable in PUT, we'll just track it locally
                // The checkbox will still work for validation, but state won't persist to backend
                return of(null);
            })
        );
    }

    /**
     * Get a question's details including notApplicable field
     * @param questionNum The question number
     * @returns Observable with question data including text, type, answer, notApplicable, and allowNA
     */
    getQuestion(questionNum: number): Observable<any> {
        this.getUserData();
        let headers = new HttpHeaders({
            token: this.localUser.token,
        });
        return this.http.get(
            `${environment.apiUrl}/QA/${questionNum}`,
            { headers: headers }
        ).pipe(
            catchError(error => {
                console.warn(`Failed to get question ${questionNum}:`, error);
                return of(null);
            })
        );
    }
}
